import { type DataThemeOptions, type MenuOption } from '@saviynt/design-system';

import { NUMERIC_VALUE_VALIDATION } from './validation';

// URL's
export const FF_BASENAME = '/ECMv6/admin';
export const FF_FORM_BUILDER_BASENAME = '/forms';
export const FF_FORM_MANAGEMENT = `${FF_FORM_BUILDER_BASENAME}`;
export const FF_FORM_BUILDER_VIEW = `${FF_FORM_BUILDER_BASENAME}/view/:customFormUid/:urlSlug`;
export const FF_FORM_BUILDER_EDIT = `${FF_FORM_BUILDER_BASENAME}/edit/:customFormUid/:urlSlug`;
export const FF_FORM_PREVIEW_NO_ID = `${FF_FORM_BUILDER_BASENAME}/preview`;
export const FF_FORM_PREVIEW = `${FF_FORM_BUILDER_BASENAME}/preview/:customFormUid/:urlSlug`;
export const FF_FORM_MAPPING_NO_ID = `${FF_FORM_BUILDER_BASENAME}/mapping`;
export const FF_FORM_MAPPING = `${FF_FORM_BUILDER_BASENAME}/mapping/:id`;
export const FF_DYNAMIC_ATTRIBUTE_FORM = `${FF_FORM_BUILDER_BASENAME}/da`;
// Create new form
export const createFormUrl = `${FF_BASENAME}${FF_FORM_BUILDER_BASENAME}/create`;
// Help documentation URL
// TODO: Needs new URL for DEFAULT_VALUE_HELP_URL, this is a placeholder only
export const DEFAULT_VALUE_HELP_URL = 'http://help.saviynt.com';

// Public/Unauthenticated Form Routes
export const FF_PUBLIC_BASENAME = '/ECMv6/forms/public';
export const FF_PUBLIC_FORM_ROUTE = `${FF_PUBLIC_BASENAME}/:customFormUid/:id`;

// Orchestration constants
export const ORCHESTRATION_INVOKE_METHOD = {
  TRIGGER: 'Trigger',
} as const;

export const ORCHESTRATION_CATEGORY = {
  SIGNAL: 'ORC_SIGNAL',
} as const;

// Action types that support data loading for form prefilling
export const DATA_LOADING_ACTIONS = [
  'GET_ENTITY_DETAILS',
  'LOAD_ENTITY_DATA',
  'FETCH_ENTITY_INFO',
  'RETRIEVE_ENTITY_DATA',
  'UPDATE_ENTITY',
  'EDIT_ENTITY',
] as const;

// Configuration for data loading behavior
export const DATA_LOADING_CONFIG = {
  // Default behavior: only load data for specific actions
  ENABLE_FOR_ACTIONS: DATA_LOADING_ACTIONS,
  // Alternative: load data for all actions except excluded ones
  EXCLUDE_ACTIONS: ['CREATE_ENTITY', 'DELETE_ENTITY'] as const,
  // Whether to check action configuration or always load when entityId is present
  CHECK_ACTION_CONFIG: true,
} as const;

// Shorthand for the color tokens to reduce code clutter
export const TOKENS = {
  // Foreground
  CF_PRI_BLD: '--color-foreground-primary-bold',
  CF_PRI_MED: '--color-foreground-primary-medium',
  CF_SEC_BLD: '--color-foreground-secondary-bold',
  CF_SEC_MED: '--color-foreground-secondary-medium',
  CF_SEC_STL: '--color-foreground-secondary-subtle',
  CF_NEU_BLD: '--color-foreground-neutral-bold',
  CF_NEU_MED: '--color-foreground-neutral-medium',
  CF_NEU_STL: '--color-foreground-neutral-subtle',
  CF_NEU_INV: '--color-foreground-neutral-inverse',
  CF_CRT_BLD: '--color-foreground-critical-bold',
  CF_CRT_MED: '--color-foreground-critical-medium',
  CF_WRN_BLD: '--color-foreground-warning-bold',
  CF_WRN_STL: '--color-foreground-warning-subtle',
  CF_SUC_MED: '--color-foreground-success-medium',
  CF_SUC_BLD: '--color-foreground-success-bold',
  CF_INF_MED: '--color-foreground-info-medium',
  CF_INF_STL: '--color-foreground-info-subtle',
  // Background
  CB_INF_STL: '--color-background-info-subtle',
};

/** Auto-dismiss (ms) for error-style `StackingAlertBanner` (Critical / Warning). Success banners omit this and keep the design-system default (~5s). */
export const ERROR_ALERT_AUTO_DISMISS_MS = 30_000;

export const SYSTEM_DATA_THEMES = {
  LIGHT: 'light' as const satisfies DataThemeOptions,
  DARK: 'dark' as const satisfies DataThemeOptions,
} as const;

// Media Queries
/**
 * Provides a guide for UX design breakpoints and their corresponding naming structure.
 * This is a reference for responsive design, indicating the pixel width and associated
 * designations for various device sizes.
 *
 * Mobile:
 * - 360px: 140 MobileXSmall (This is below our minimum, but it’s a common Android size, adding it here just in case)
 * - 375px: 130 MobileSmall
 * - 480px: 120 MobileLarge
 * - 600px: 110 MobileXLarge
 *
 * Tablet:
 * - 768px: 100 TabletXSmall
 * - 834px: 90 TabletSmall
 * - 900px: 80 TabletMedium
 * - 960px: 70 TabletLarge
 * - 1024px: 60 TabletXLarge
 *
 * Desktop:
 * - 1280px: 50 DesktopSmall
 * - 1366px: 40 DesktopMedium
 * - 1440px: 30 DesktopLarge
 * - 1600px: 20 DesktopXLarge
 * - 1920px: 10 DesktopXXLarge
 */
export const SCREEN_SIZE_MOBILE_XLARGE = 'MobileXLarge';
export const SCREEN_SIZE_TABLET_XSMALL = 'TabletXSmall';
export const SCREEN_SIZE_DESKTOP_SMALL = 'DesktopSmall';

export const SCREEN_WIDTH_QUERIES = [
  { query: '(max-width: 599px)', value: 110, label: SCREEN_SIZE_MOBILE_XLARGE },
  { query: '(max-width: 767px)', value: 100, label: SCREEN_SIZE_TABLET_XSMALL },
  { query: '(max-width: 1279px)', value: 50, label: SCREEN_SIZE_DESKTOP_SMALL },
];

export const EXPRESSIONS_TYPES = {
  REQUIRED: 'REQUIRED',
  VISIBILITY: 'VISIBILITY',
  EDITABLE: 'EDITABLE',
} as const;
export const VISIBILITY_EXPRESSION_TYPES = {
  SHOW: 'SHOW',
  HIDE: 'HIDE',
} as const;

export const REQUIRED_EXPRESSION_TYPES = {
  REQUIRED: 'REQUIRED',
  NOT_REQUIRED: 'NOT_REQUIRED',
} as const;

export const EDITABLE_EXPRESSION_TYPES = {
  READONLY: 'READONLY',
  EDITABLE: 'EDITABLE',
} as const;

export const FORM_STATUS = {
  DRAFT: 'DRAFT',
  PUBLISHED: 'PUBLISHED',
  DISABLED: 'DISABLED',
  ARCHIVED: 'ARCHIVED',
} as const;

export const FIELD_PROPERTIES = {
  LABEL: 'label',
  ELEMENT_ID: 'elementId',
  PLACEHOLDER: 'placeholder',
  HELPER_TEXT: 'helptext',
  TOOLTIP: 'tooltip',
  REQUIRED_ERROR_MESSAGE: 'requireErrorMessage',
  MIN_MAX_ERROR_MESSAGE: 'minMaxErrorMessage',
  REGEX_ERROR_MESSAGE: 'regexErrorMessage',
  REGEX_PATTERN: 'regexPattern',
  FIXED_GROUP_OPTION_LABEL: 'fixedGroupOptionLabel',
  FIXED_GROUP_OPTION_VALUE: 'fixedGroupOptionValue',
  SHOW_WHEN_EXPRESSION: 'showWhenExpression',
  HIDE_WHEN_EXPRESSION: 'hideWhenExpression',
  REQUIRED_WHEN_EXPRESSION: 'requiredWhenExpression',
  NOT_REQUIRED_WHEN_EXPRESSION: 'notRequiredWhenExpression',
  READ_ONLY_EXPRESSION: 'readOnlyExpression',
  EDITABLE_EXPRESSION: 'editableExpression',
  DEFAULT_VALUE_EXPRESSION: 'defaultValueExpression',
  EXPRESSION_VALIDATION: 'expressionValidation',
  EXPRESSION_ERROR_MESSAGE: 'expressionErrorMessage',
  TNC_TYPE: 'tncType',
  TNC_LINK: 'tncLink',
  TNC_MESSAGE: 'tncMessage',
  RICHTEXT: 'richText',
  UNIFIED_LIMIT: 'unifiedLimit',
} as const;

export const FORM_PROPERTIES = {
  FORM_NAME: 'formName',
  DESCRIPTION: 'description',
  URL_SLUG: 'urlSlug',
  TARGET_ENTITY: 'targetEntity',
  FORM_ACTION: 'formAction',
  WORKFLOW: 'approvalWorkflow',
};

export const LAYOUT_TYPES = {
  SECTION: 'section',
  COLUMN: 'column',
  ROW: 'row',
  FIELD: 'field',
} as const;

export const FIELD_TYPES = {
  RICHTEXT: 'richtext',
  SECTION: 'section',
  TEXT: 'text',
  TEXTAREA: 'textarea',
  SINGLESELECT: 'singleselect',
  MULTISELECT: 'multiselect',
  DATATABLE: 'datatable',
  RADIO: 'radio',
  CHECKBOX: 'checkbox',
  DATE: 'date',
  DATETIME: 'datetime',
  SPACER: 'spacer',
  NUMBER: 'number',
  BOOLEAN: 'boolean',
  SENSITIVE: 'sensitive',
  PASSWORD: 'password',
  TERMSANDCONDITIONS: 'tnc',
  CAPTCHA: 'captcha',
  // FULLNAME: 'fullname',
  // ADDRESS: 'address',
  // PHONENUMBER: 'phonenumber',
  // SIGNATURE: 'signature',
};

/** Backend sentinel value for sensitive/password fields in update scenarios. */
export const SENSITIVE_FIELD_MASK = '##########';

/** Default success messages for form submission, keyed by form type. */
export const DEFAULT_SUCCESS_MESSAGES = {
  /** Authenticated forms (page-based and link-based) */
  AUTHENTICATED: 'Your request has been successfully submitted.',
  /** Public/unauthenticated forms */
  PUBLIC: 'Success! Your request has been submitted.',
} as const;

/** Default submit button label */
export const DEFAULT_SUBMIT_BTN_LABEL = 'Submit';

export const VALIDATION_TYPES = {
  REQUIRED: 'REQUIRED',
  REGEX: 'REGEX',
  MIN_LENGTH: 'MINLENGTH',
  MAX_LENGTH: 'MAXLENGTH',
  MIN_CHAR_COUNT: 'MIN',
  MAX_CHAR_COUNT: 'MAX',
  MIN_VALUE: 'MIN',
  MAX_VALUE: 'MAX',
  EXPRESSION: 'EXPRESSION',
} as const;

export const EXPRESSION_INPUT_TYPES = {
  REQUIRED: 'REQUIRED',
  READ_ONLY: 'READ_ONLY',
  RICHTEXT: 'RICHTEXT',
} as const;

export const SPACER = {
  DEFAULT_HEIGHT: '20',
  MIN_HEIGHT: 1,
  MAX_HEIGHT: 200,
} as const;

export const TEXTAREA = {
  DEFAULT_ROWS: 4,
  MIN_ROWS: 2,
  MAX_ROWS: 20,
} as const;

export const DATE_FORMATS = {
  MM_DD_YYYY: {
    display: 'MM-DD-YYYY',
    picker: 'MM-dd-yyyy',
  },
  DD_MM_YYYY: {
    display: 'DD-MM-YYYY',
    picker: 'dd-MM-yyyy',
  },
  YYYY_DD_MM: {
    display: 'YYYY-DD-MM',
    picker: 'yyyy-dd-MM',
  },
} as const;

// Helper to get all format keys
export const DATE_FORMAT_KEYS = Object.keys(
  DATE_FORMATS
) as (keyof typeof DATE_FORMATS)[];

export const DATE_RANGE_LIMITS = {
  // Number of years to subtract/add from today for default date ranges
  DEFAULT_MIN_YEARS_BACK: 100, // Default minimum: today - 100 years
  DEFAULT_MAX_YEARS_FORWARD: 100, // Default maximum: today + 100 years

  // Date picker limits for properties panel
  PICKER_MIN_YEARS_BACK: 100, // Properties panel min date picker: today - 100 years
  PICKER_MAX_YEARS_FORWARD: 100, // Properties panel max date picker: today + 100 years
} as const;

export const CHARACTER_COUNT = {
  MIN: 0,
  MAX: 500,
  TEXT_MAX: 500,
  TEXTAREA_MAX: 2000,
  ERROR_MESSAGES: {
    MIN_GREATER_THAN_MAX: 'Minimum count cannot be greater than maximum count.',
  },
} as const;

export const NUMERICAL_VALUE = {
  MIN: 0,
  MAX: NUMERIC_VALUE_VALIDATION.MAX_VALUE,
  ERROR_MESSAGES: {
    MIN_GREATER_THAN_MAX: 'Minimum value cannot be greater than maximum value.',
  },
} as const;

export const ALIGNMENT_PROPERTY_ORIENTATION = {
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right',
} as const;

export const TNC_TYPES = {
  TEXT: 'text',
  URL: 'url',
} as const;

export const BUILDER_ACCESS_STATES = {
  VIEW: 'view',
  EDIT: 'edit',
  DISABLED: 'disabled',
} as const;

export const READ_ONLY_EMPTY_OPTIONS: MenuOption[] = [
  { label: '-', value: 'empty', isChecked: true },
];

export const DEFAULT_VALUE_MODES = {
  TEXT: 'text',
  NUMERIC: 'numeric',
  BOOLEAN: 'boolean',
} as const;

// Default status values that exclude archived forms
export const DEFAULT_STATUS_VALUES = [
  'DRAFT',
  'DISABLED',
  'PUBLISHED',
] as string[];

// FLAGS
export const ENABLE_DATA_TABLE = false;

/** Show the DA Test button on Form Management (default off). */
export const ENABLE_DA_TEST_BUTTON = false;

export const IDENTITIES_URL = '/ECMv6/external/dashboard';
export const MY_REQUESTS_URL =
  '/ECM/jbpmworkflowmanagement/showmyhistoryrequests';

export const QUILL_THEMES = {
  SNOW: 'snow',
  BUBBLE: 'bubble',
} as const;

export const TNC_MESSAGE_PATTERN = /^\s*(.*?)\{([^{}]+)}(.*)$/;
export const MAX_TNC_MESSAGE_CHAR_COUNT = 150;
export const MAX_RICH_TEXT_CHAR_COUNT = 5000;
export const MAX_TNC_CHAR_COUNT = 30000;

/**
 * Configuration for Dynamic Attribute (DA) forms.
 * DA forms are identified by urlSlug or customFormUid starting with the PREFIX.
 * These forms have different layout and feature requirements compared to normal forms.
 */
export const DYNAMIC_ATTRIBUTE_FORM_CONFIG = {
  /**
   * Prefix used to identify Dynamic Attribute forms.
   * If urlSlug or customFormUid starts with this prefix, it's a DA form.
   */
  PREFIX: 'sav-da',

  /**
   * Maximum columns for Dynamic Attribute forms.
   * DA forms support up to 3 columns, while regular forms use 4.
   */
  MAX_COLUMNS: 3,

  /**
   * Enables HTML tag support for labels in Dynamic Attribute forms.
   * When true, labels can contain a limited set of HTML tags for rich text formatting.
   */
  ENABLE_HTML_LABELS: true,
} as const;

/**
 * Global configuration for public form inactivity timeout.
 * When a user is idle on a public (unauthenticated) form for the configured
 * duration, the session expires and the form becomes inaccessible.
 *
 * - TIMEOUT_MINUTES: Total idle time before session expires
 * - WARNING_THRESHOLD: Fraction of time elapsed before showing a warning (0.5 = 50%)
 * - ACTIVITY_EVENTS: DOM events that count as user activity and reset the idle timer
 */
export const INACTIVITY_TIMEOUT_CONFIG = {
  TIMEOUT_MINUTES: 20,
  WARNING_THRESHOLD: 0.5,
  ACTIVITY_EVENTS: ['mousedown', 'keydown', 'scroll', 'touchstart'] as const,
} as const;

export const DROPDOWN_DEFAULT_VALUE = {
  MAX_OPTIONS: 5,
} as const;

export const DUPLICATE_CHECK = {
  TYPE: 'DUPLICATE_CHECK',
  MAX_SINGLE_KEYS: 4,
  MAX_COMPOSITE_KEYS: 1,
  MAX_FIELDS_PER_COMPOSITE: 3,
  SUPPORTED_CATEGORIES: ['CREATE_USER_REQUEST', 'UPDATE_USER_REQUEST'],
  SUPPORTED_FIELD_TYPES: ['text', 'number'],
} as const;
